import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/warehouses")({
  component: WarehousesPage,
});

import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchWarehouses,
  useWarehouseMutations,
  type WarehouseOrdering,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { WarehouseTable } from "@/components/inventory/tables/WarehouseTable";
import { WarehouseFormSheet } from "@/components/inventory/forms/WarehouseFormSheet";
import type { Warehouse, WarehousePayload } from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function WarehousesPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.warehouse.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // FILTERS + ORDERING
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        location: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      WarehouseOrdering
    >({
      name: null,
      location: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | undefined
  >();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Warehouse | null>(null);

  // QUERIES & MUTATIONS
  const warehousesQuery = useFetchWarehouses({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "true"
        ? true
        : statusFilter === "false"
          ? false
          : undefined,
  });
  const warehouses = warehousesQuery.data?.results ?? [];
  const count = warehousesQuery.data?.count ?? 0;

  const { createWarehouse, updateWarehouse, deleteWarehouse } =
    useWarehouseMutations();

  const isMutating =
    createWarehouse.isPending ||
    updateWarehouse.isPending ||
    deleteWarehouse.isPending;
  const isLoading = warehousesQuery.isPending || isMutating;
  const isFetching = warehousesQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const resetFilters = () => {
    setOrdering({
      name: null,
      location: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: WarehousePayload) => {
    if (editing) {
      await updateWarehouse.mutateAsync({ id: editing.id, payload: data });
      toast.success("Warehouse updated");
    } else {
      await createWarehouse.mutateAsync(data);
      toast.success("Warehouse created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWarehouse.mutateAsync({ id: deleteTarget.id });
      toast.success("Warehouse deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Warehouses"
          description="Manage storage locations and warehouses"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsViewOnly(false);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Add Warehouse"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search warehouses..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
        </FiltersPanel>

        <WarehouseTable
          warehouses={warehouses}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(w) =>
            navigate({
              to: "/admin/inventory/warehouses/$id",
              params: { id: String(w.id) },
            })
          }
          onView={(w) => {
            setEditing(w);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(w) => {
            setEditing(w);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(w) => setDeleteTarget(w)}
          canManage={canManage}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <WarehouseFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Warehouse"
          description={`Delete warehouse "${deleteTarget?.name}"? All associated stock data will be affected.`}
          itemName={deleteTarget?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
